import { FC, useMemo, useState } from "react";
import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import CustomPopup from "../../../components/CustomPopup/CustomPopup";
import "./LanguageSelectionPopup.scss";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux-store/CustomReduxHooks";
import {
  selectActiveLanguage,
  setLanguage,
} from "../../../redux-store/reducers/LanguageSlice";
import { isEmpty } from "lodash";
import { useAppTranslation } from "../../../hooks/useAppTranslation";
import { useLazyUpdateUserProfileDetailsQuery } from "../../../features/Feature_GeneralAppElements/GeneralAppElements_Api";
import { API_STATUS_CODE } from "../../../utils/ApiEndpoints";
import { updateUserProfileDetails } from "../../../redux-store/reducers/CsAppSlice";
import CustomCircularLoader from "../../../components/CustomCircularLoader/CustomCircularLoader";

interface Props {
  open?: boolean;
  handleOpen?: Function;
}

const LanguageSelectionPopup: FC<Props> = ({
  open = false,
  handleOpen = () => {},
}) => {
  const { t } = useAppTranslation("common");
  const dispatch = useAppDispatch();
  const selectedLanguage = useAppSelector(selectActiveLanguage);
  const [selectedLang, setSelectedLang] = useState<string>(selectedLanguage);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const pageVariables = useAppSelector(
    (state) => state.CmsSlice.CmsPageVariableData,
  );
  const csAppSlice = useAppSelector((state) => state.CsAppSlice);

  const [updateUserProfileDetailsApi] = useLazyUpdateUserProfileDetailsQuery();

  const languageOptionsData = useMemo(() => {
    return (
      pageVariables.filter(
        (i) => i.attributes.cmsType === "language-options",
      ) || []
    );
  }, [pageVariables]);

  const handleLanguageSelection = (language: string) => {
    setSelectedLang(language);
  };

  const handleContinue = async () => {
    if (isEmpty(selectedLang)) return;
    try {
      dispatch(setLanguage(selectedLang));
      const userDetails = csAppSlice.userDetails;
      let reqPayload = {
        csUserId: csAppSlice.csUserId,
        name: userDetails?.name,
        pan: userDetails?.pan,
        dob: userDetails?.dob,
        mobile: userDetails?.mobileNumber,
        emailId: userDetails?.emailId,
        preferredLanguage: selectedLang,
      };
      setIsLoading(true);
      await updateUserProfileDetailsApi(reqPayload).then((res) => {
        if (res.isSuccess && res.data?.code === API_STATUS_CODE.SUCCESS) {
          let updatedData = { ...userDetails, ...reqPayload };
          dispatch(updateUserProfileDetails(updatedData));
        }
        setIsLoading(false);
      });
    } catch (error) {
      console.error("Unable to save language to localStorage", error);
    }
    handleOpen(false);
  };

  return (
    <CustomPopup
      className="language-selection-popup"
      open={open}
      handleOpen={handleOpen}
    >
      <Box>
        <Typography
          variant="h5"
          letterSpacing="1.5px"
          lineHeight="120%"
          textTransform="uppercase"
          textAlign="center"
          marginBottom=".5rem"
        >
          {t("languageSettings.title")}
        </Typography>
        <Typography textAlign="center" variant="subtitle2" lineHeight="120%">
          {t("languageSettings.subtitle")}
        </Typography>
        <Box marginY="1.5rem">
          <Grid container spacing={2}>
            {languageOptionsData.map((option) => {
              const backgroundImage =
                option.attributes.mobileImage?.data?.attributes?.url;
              return (
                <Grid item xs={6}>
                  <Box
                    className="language-box"
                    onClick={() =>
                      handleLanguageSelection(
                        option.attributes?.pageReference || "en",
                      )
                    }
                    sx={{
                      backgroundImage: isEmpty(backgroundImage)
                        ? "none"
                        : `url(${backgroundImage})`,
                      border:
                        option.attributes?.pageReference === selectedLang
                          ? "1px solid  #C7222A"
                          : "1px solid #ffffff",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <Box>
                        <Typography
                          variant="h4"
                          lineHeight="120%"
                          marginBottom=".125rem"
                        >
                          {option.attributes.title}
                        </Typography>
                        <Typography variant="subtitle2" lineHeight="130%">
                          {option.attributes.text}
                        </Typography>
                      </Box>
                      <img
                        src={
                          option.attributes.forwardArrowIcon?.data?.attributes
                            ?.url
                        }
                        alt={"arrow"}
                      />
                    </Stack>
                    <img
                      src={option.attributes.infographic?.data?.attributes?.url}
                      alt="infographic"
                      className="language-infographic-image"
                    />
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
        <Button
          variant="contained"
          fullWidth
          onClick={handleContinue}
          disabled={isEmpty(selectedLang)}
        >
          {isLoading ? (
            <CustomCircularLoader size={20} color="white" />
          ) : (
            t("languageSettings.actionButtons.continue")
          )}
        </Button>
      </Box>
    </CustomPopup>
  );
};

export default LanguageSelectionPopup;
